import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useContentToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

interface HeroSolidBGProps {
  tokenPrefix?: string;
  title?: string;
  blurbField?: string;
  defaultText?: string;
}

export function HeroSolidBG({
  tokenPrefix,
  title = 'Page',
  blurbField,
  defaultText,
}: HeroSolidBGProps) {
  const prefix = tokenPrefix || 'herosolidbg';
  const titlePath = `${prefix}.title`;
  const blurbPath = `${prefix}.blurb`;
  const bgImagePath = `${prefix}.bg_image`;

  // Background image defaults to the property's featured image so every inner
  // page gets a photographic hero out of the box; editors can swap it per page.
  const bgImageDef: SlotGroupDefinition = {
    label: 'Background Image',
    fields: {
      image: {
        label: 'Image',
        type: 'image',
        defaultField: 'property_featured_image',
      },
      alt: {
        label: 'Alt Text',
        type: 'text',
        defaultValue: 'Page hero image',
      },
    },
  };

  const bgImage = useSlotGroup(bgImagePath, bgImageDef);

  // Collapse the blurb band entirely when there's no blurb content, so
  // title-only pages (sitemap, accessibility, privacy) don't render an empty
  // band of padding above the page content.
  const blurbValue = useContentToken(blurbPath, {
    defaultField: blurbField,
    defaultValue: defaultText || '',
  });
  const hasBlurb =
    !!blurbValue && blurbValue.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim().length > 0;

  return (
    <>
      {/* Photographic hero band — title overlaid on a darkened image. */}
      <section
        className="relative w-full overflow-hidden flex items-center justify-center text-center min-h-[320px] md:min-h-[420px] pb-12 md:pb-16"
        style={{
          paddingTop: 'calc(var(--offer-banner-height, 0px) + 6rem)',
          transition: 'padding-top 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {/* Background image */}
        <div className="absolute inset-0">
          <EditableSlotGroup groupPath={bgImagePath} definition={bgImageDef}>
            {bgImage.image ? (
              <VibesitesImage
                url={bgImage.image}
                alt={bgImage.alt || ''}
                priority
                className="absolute inset-0 w-full h-full object-cover"
                sizes="100vw"
                objectFit="cover"
              />
            ) : (
              <div className="absolute inset-0 bg-primary" />
            )}
          </EditableSlotGroup>
        </div>

        {/* Dark overlay for legibility */}
        <div className="absolute inset-0 bg-black/45 pointer-events-none" />

        {/* Page Title */}
        <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <RichTextContent
            path={titlePath}
            defaultValue={`<p>${title}</p>`}
            as="h1"
            className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-[0.2em] leading-tight break-words text-white drop-shadow-lg"
          />
        </div>
      </section>

      {/* Blurb section — clean light band below the hero image. Collapsed when
          the page has no blurb so we don't stack empty padding. */}
      {hasBlurb && (
        <section className="bg-background py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <RichTextContent
              path={blurbPath}
              defaultField={blurbField}
              defaultValue={defaultText || ''}
              as="div"
              className="font-body text-base md:text-lg leading-relaxed text-[var(--vibe-color-text)] [&_a]:text-primary [&_a]:underline"
            />
          </div>
        </section>
      )}

      {/* Schema-lint anchors — hidden, never interactive */}
      <RichTextContent
        path="herosolidbg.title"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="herosolidbg.blurb"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="herosolidbg.bg_image"
        definition={bgImageDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HeroSolidBG',
  displayName: 'Hero – Solid Background',
  tokens: {
    'herosolidbg.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Page</p>',
    },
    'herosolidbg.blurb': {
      label: 'Page Blurb',
      type: 'richtext',
      defaultValue: '',
    },
  },
  slotGroups: {
    'herosolidbg.bg_image': {
      label: 'Background Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultField: 'property_featured_image',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultValue: 'Page hero image',
        },
      },
    },
  },
};

(HeroSolidBG as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HeroSolidBG;
